import React from 'react';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import striptags from 'striptags';
import { Box } from '@bridebook/ui';
import type { Element, Node, NodeWithChildren, TextNode } from '../../../types/dom';
import { Link } from '../../components/link/link';

const htmlToReactParser = new Parser();
const processNodeDefinitions = new ProcessNodeDefinitions(React);

const DEFAULT_ALLOWED_TAGS = '<p><h3><ul><ol><li><strong><b><em><u><a><br>'; // h3 kept for legacy venue description rendering

/**
 * Renders HTML content as React components, stripping tags to set of allowed tags,
 * optionally stripping links attributes.
 */
export const renderHtml = (
  htmlContent: string | undefined,
  options?: {
    allowedTags?: string | string[];
    cleanLinks?: boolean;
    reducedSpacing?: boolean;
  },
): null | any => {
  if (!htmlContent?.trim()) {
    return null;
  }
  const { allowedTags, cleanLinks, reducedSpacing } = options || {};

  const processingInstructions: any[] = [
    {
      shouldProcessNode: (node: Element) => cleanLinks && isTagOf(node, 'a'),
      processNode: (node: Element) => convertAnchorToLink(node),
    },
    {
      shouldProcessNode: () => true,
      processNode: processNodeDefinitions.processDefaultNode,
    },
  ];

  if (reducedSpacing) {
    htmlContent = htmlContent
      ?.replace(/\n{3,}/g, '\n\n')
      ?.replace(/^\n{2}/, '')
      ?.replace(/\n{2}$/, '');
  }

  const contentMarkup = cleanHtml(htmlContent, allowedTags);

  return htmlToReactParser.parseWithInstructions(contentMarkup, () => true, processingInstructions);
};

const cleanHtml = (content: string, allowedTags?: string | string[]): string =>
  striptags(content, allowedTags || DEFAULT_ALLOWED_TAGS);

const isTagOf = (domNode: Element, tagName?: string) => {
  const { name, children } = domNode;
  if (domNode.type === 'tag' && name && children) {
    return name === tagName;
  }
  return false;
};

// Type guard function to identify Text node
const isTextNode = (node: Node): node is TextNode => node.type === 'text';

const renderChildrenText = (children: NodeWithChildren[]): string =>
  children.reduce((result, child) => {
    if (isTextNode(child)) {
      result += child.data;
    } else if (child.children?.length > 0) {
      result += renderChildrenText(child.children as NodeWithChildren[]);
    }
    return result;
  }, '');

const convertAnchorToLink = (domNode: Element) => {
  const {
    attribs: { href },
    children,
  } = domNode;
  const firstChild = domNode.firstChild;
  const tag = firstChild as Element;

  const commonAttribs = { href };

  if (tag && tag.name === 'img') {
    return (
      <Box display="block" {...commonAttribs} as="a">
        <img className={tag.attribs.class} src={tag.attribs.src} alt={tag.attribs.alt} />
      </Box>
    );
  }

  if (children?.length > 0) {
    const linkText = renderChildrenText(children as NodeWithChildren[]);
    return (
      <Link {...commonAttribs} key={(firstChild as TextNode).data} prefetch={false}>
        {linkText}
      </Link>
    );
  }

  return null;
};
